/**
 * cloud_tunnel.ts — the planetary-transition cloud layer.
 *
 * A camera-following shell of soft cloud sprites plus a screen white-out
 * overlay. It is the atmosphere↔space "live loading screen" (the industry
 * pattern: the cloud band hides the world swap and the streaming pop-in):
 *
 *  · ASCENT: AtlasVehicleModule drives `setAmbient()` from altitude — the
 *    sky thickens through the cloud band, whites out fully before the
 *    ascendToSpace handoff, and `reveal()` fades space in once the sector
 *    has frames under it.
 *  · DESCENT: SpaceModule `hold()`s the tunnel before planetfall, the world
 *    swap + terrain streaming happen behind the white, and `reveal()`
 *    breaks the ship out of the cloud deck into the open sky.
 *
 * MATERIAL/LIGHT LAW: every GPU object (one CanvasTexture, ONE shared
 * SpriteMaterial, a fixed sprite pool) is built ONCE at first use and only
 * toggled by opacity/visibility afterwards — no per-transition allocation,
 * no lights, no pipeline churn. The DOM overlay does the guaranteed full
 * white-out for free.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App } from 'enginex';

const SPRITES = 18;
const SHELL_MIN = 45;   // m from the camera
const SHELL_MAX = 150;
const AMBIENT_TTL_S = 0.6; // ambient decays when no flyer refreshes it

class CloudTunnel {
  private group: THREE.Group | null = null;
  private mat: THREE.SpriteMaterial | null = null;
  private tex: THREE.CanvasTexture | null = null;
  private dom: HTMLDivElement | null = null;
  private drift: Array<{ v: THREE.Vector3; spin: number }> = [];

  private ambient = 0;
  private ambientTtl = 0;
  /** Scripted envelope: -1 = none; else current scripted intensity. */
  private script = -1;
  private scriptRampS = 0.8;
  /** reveal() plan: hold full for `holdS`, then fade over `fadeS`. */
  private revealHold = 0;
  private revealFade = 0;
  private revealing = false;
  private eff = 0;

  /** Altitude-driven intensity (0..1); expires unless refreshed (~each tick). */
  setAmbient(v: number): void {
    this.ambient = Math.max(0, Math.min(1, v));
    this.ambientTtl = AMBIENT_TTL_S;
  }

  /** Scripted full white-out (world-swap cover). Stays until reveal/cancel. */
  hold(rampS = 0.8): void {
    if (this.script < 0) this.script = this.eff;
    this.scriptRampS = Math.max(0.1, rampS);
    this.revealing = false;
  }

  /** After `holdS` more seconds of full cover, fade out over `fadeS`. */
  reveal(holdS = 2.5, fadeS = 3.0): void {
    if (this.script < 0) this.script = Math.max(this.eff, 0.001);
    this.revealHold = holdS;
    this.revealFade = Math.max(0.2, fadeS);
    this.revealing = true;
  }

  /** Drop the scripted envelope (a refused transition). */
  cancel(): void {
    this.script = -1;
    this.revealing = false;
  }

  get scripted(): boolean { return this.script >= 0; }

  /** Current effective white-out 0..1 (drives the wind-rush SFX layer). */
  get intensity(): number { return this.eff; }

  /** Drive every frame (AtlasVehicleModule.frame — it ticks in every world). */
  update(dt: number, app: App | null): void {
    // Envelope math first — it must advance even before the visuals exist.
    if (this.ambientTtl > 0) this.ambientTtl -= dt;
    else this.ambient = Math.max(0, this.ambient - dt * 1.5);
    if (this.script >= 0) {
      if (this.revealing) {
        if (this.revealHold > 0) {
          this.revealHold -= dt;
          this.script = Math.min(1, this.script + dt / this.scriptRampS);
        } else {
          this.script -= dt / this.revealFade;
          if (this.script <= 0) { this.script = -1; this.revealing = false; }
        }
      } else {
        this.script = Math.min(1, this.script + dt / this.scriptRampS);
      }
    }
    const target = Math.max(this.ambient, this.script < 0 ? 0 : this.script);
    // Fast attack, gentle release — clouds swallow quickly, part slowly.
    const rate = target > this.eff ? 2.5 : 1.2;
    this.eff += (target - this.eff) * Math.min(1, dt * rate);
    if (target === 0 && this.eff < 0.004) this.eff = 0;

    if (this.eff <= 0) {
      if (this.group) this.group.visible = false;
      if (this.dom) this.dom.style.display = 'none';
      return;
    }
    if (!app) return;
    this.ensure(app);
    const group = this.group;
    const mat = this.mat;
    const dom = this.dom;
    if (!group || !mat || !dom) return;

    // Follow the camera in ABSOLUTE coords (children of the rebased scene
    // carry absolute positions — the AtlasWaterModule camera pattern).
    let cam: THREE.Camera | undefined;
    try { cam = app.renderer.cameras.main; } catch { cam = undefined; }
    if (cam) {
      const p = new THREE.Vector3();
      cam.getWorldPosition(p);
      p.x -= app.world.scene.position.x;
      p.y -= app.world.scene.position.y;
      p.z -= app.world.scene.position.z;
      group.position.copy(p);
    }
    group.visible = true;
    // Slow drift keeps the deck alive; wrap wanderers back into the shell.
    const kids = group.children;
    for (let i = 0; i < kids.length; i++) {
      const s = kids[i];
      const d = this.drift[i];
      s.position.addScaledVector(d.v, dt);
      (s as THREE.Sprite).material.rotation += d.spin * dt;
      const r = s.position.length();
      if (r > SHELL_MAX * 1.25) s.position.multiplyScalar(SHELL_MIN / r);
    }
    mat.opacity = Math.min(0.92, this.eff);
    dom.style.display = 'block';
    dom.style.opacity = String(Math.min(0.97, this.eff * 1.06));
  }

  /** Build the GPU/DOM objects once. */
  private ensure(app: App): void {
    if (this.group) return;
    // Soft cloud puff: overlapping radial gradients on one canvas.
    const c = document.createElement('canvas');
    c.width = c.height = 256;
    const g = c.getContext('2d')!;
    g.clearRect(0, 0, 256, 256);
    for (let i = 0; i < 9; i++) {
      const x = 70 + Math.random() * 116, y = 86 + Math.random() * 84;
      const r = 46 + Math.random() * 58;
      const grad = g.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, 'rgba(244,247,250,0.55)');
      grad.addColorStop(0.6, 'rgba(236,241,246,0.28)');
      grad.addColorStop(1, 'rgba(236,241,246,0)');
      g.fillStyle = grad;
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
    }
    this.tex = new THREE.CanvasTexture(c);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.mat = new THREE.SpriteMaterial({
      map: this.tex,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false, // the deck covers EVERYTHING — that is its job
    });
    markMrtOverlay(this.mat);
    this.group = new THREE.Group();
    this.group.name = 'CloudTunnel';
    this.group.renderOrder = 9000;
    for (let i = 0; i < SPRITES; i++) {
      const s = new THREE.Sprite(this.mat);
      const a = Math.random() * Math.PI * 2;
      const e = (Math.random() - 0.5) * Math.PI;
      const r = SHELL_MIN + Math.random() * (SHELL_MAX - SHELL_MIN);
      s.position.set(Math.cos(a) * Math.cos(e) * r, Math.sin(e) * r * 0.7, Math.sin(a) * Math.cos(e) * r);
      s.scale.setScalar(90 + Math.random() * 140);
      s.renderOrder = 9000 + i;
      this.group.add(s);
      this.drift.push({
        v: new THREE.Vector3((Math.random() - 0.5) * 7, (Math.random() - 0.5) * 2.5, (Math.random() - 0.5) * 7),
        spin: (Math.random() - 0.5) * 0.12,
      });
    }
    this.group.visible = false;
    app.world.scene.add(this.group);

    const dom = document.createElement('div');
    dom.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:26;display:none;opacity:0;' +
      'background:radial-gradient(ellipse at 50% 55%, color-mix(in srgb, var(--ax-text) 99%, transparent) 0%, color-mix(in srgb, var(--ax-text) 97%, transparent) 62%, color-mix(in srgb, var(--ax-text) 95%, transparent) 100%);';
    document.body.appendChild(dom);
    this.dom = dom;
  }

  dispose(scene?: THREE.Scene | null): void {
    if (this.group) { (scene ?? this.group.parent)?.remove(this.group); }
    this.tex?.dispose();
    this.mat?.dispose();
    this.dom?.remove();
    this.group = null; this.mat = null; this.tex = null; this.dom = null;
    this.drift = [];
    this.ambient = 0; this.script = -1; this.eff = 0; this.revealing = false;
  }
}

/** The one tunnel — driven by AtlasVehicleModule, scripted by SpaceModule too. */
export const cloudTunnel = new CloudTunnel();
